/**
 * @module flight
 * Flight utility functions
 */

/**
 * Formats a date string to a readable time format
 * @param {string} dateString - The date string to format
 * @returns {string} The formatted time string
 */
export const formatTime = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Formats a date string to a readable date format
 * @param {string} dateString - The date string to format
 * @returns {string} The formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Formats a date string to a short date format
 * @param {string} dateString - The date string to format
 * @returns {string} The formatted short date string
 */
export const formatShortDate = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Formats duration in minutes to a readable format
 * @param {number} minutes - The duration in minutes
 * @returns {string} The formatted duration string
 */
export const formatDuration = (minutes) => {
  if (!minutes) return 'N/A'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h ${mins}m`
}

/**
 * Gets the status icon based on flight status
 * @param {string} status - The flight status
 * @returns {string} The status icon class name
 */
export const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'scheduled':
      return 'text-primary-600 dark:text-primary-400'
    case 'in flight':
    case 'inflight':
      return 'text-success-600 dark:text-success-400'
    case 'delayed':
      return 'text-warning-600 dark:text-warning-400'
    case 'cancelled':
      return 'text-red-600 dark:text-red-400'
    case 'completed':
      return 'text-success-600 dark:text-success-400'
    default:
      return 'text-neutral-500 dark:text-neutral-400'
  }
}

/**
 * Gets the status color classes based on flight status
 * @param {string} status - The flight status
 * @returns {string} The status color classes
 */
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'scheduled':
      return 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400'
    case 'in flight':
    case 'inflight':
      return 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
    case 'delayed':
      return 'bg-warning-100 dark:bg-warning-900 text-warning-600 dark:text-warning-400'
    case 'cancelled':
      return 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
    case 'completed':
      return 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
    default:
      return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
  }
}

/**
 * Gets the seat availability color based on available seats percentage
 * @param {number} seatsAvailable - Number of available seats
 * @param {number} totalSeats - Total number of seats
 * @returns {string} The color class for seat availability
 */
export const getSeatAvailabilityColor = (seatsAvailable, totalSeats) => {
  if (!seatsAvailable || !totalSeats) return 'text-neutral-500 dark:text-neutral-400'

  const percentage = (seatsAvailable / totalSeats) * 100
  if (percentage <= 10) return 'text-red-600 dark:text-red-400'
  if (percentage <= 25) return 'text-warning-600 dark:text-warning-400'
  return 'text-success-600 dark:text-success-400'
}

/**
 * Calculates flight statistics from an array of flights
 * @param {Array} flights - Array of flight objects
 * @returns {Object} Object containing flight statistics
 */
export const calculateFlightStats = (flights) => {
  const stats = {
    scheduled: 0,
    inFlight: 0,
    delayed: 0,
    cancelled: 0,
    completed: 0,
    total: flights?.length || 0,
  }

  flights?.forEach((flight) => {
    switch (flight.status?.toLowerCase()) {
      case 'scheduled':
        stats.scheduled++
        break
      case 'in flight':
      case 'inflight':
        stats.inFlight++
        break
      case 'delayed':
        stats.delayed++
        break
      case 'cancelled':
        stats.cancelled++
        break
      case 'completed':
        stats.completed++
        break
      default:
        stats.scheduled++
    }
  })

  return stats
}

/**
 * Formats a price value to currency format
 * @param {number} price - The price value
 * @param {string} currency - The currency code (default: 'USD')
 * @returns {string} The formatted price string
 */
export const formatPrice = (price, currency = 'USD') => {
  if (!price) return 'N/A'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(price)
}
